import React, { useState, useRef, useEffect } from 'react';
import { api } from '../../config/api';
import { useAuth } from './AuthContext';
import { useNotification } from '../shared/NotificationContext';

const VerificationCodeInput = ({ onComplete, error }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [showIncompleteError, setShowIncompleteError] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    
    if (pastedData.length === 6 && /^\d+$/.test(pastedData)) {
      const digits = pastedData.split('');
      setCode(digits);
      setShowIncompleteError(false);
      onComplete(pastedData);
    }
  };

  const handleChange = (index, value) => {
    if (value.length > 1) {
      value = value[value.length - 1];
    }
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setShowIncompleteError(false);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }

    if (newCode.every(digit => digit !== '')) {
      onComplete(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleManualVerification = () => {
    if (code.every(digit => digit !== '')) {
      onComplete(code.join(''));
    } else {
      setShowIncompleteError(true);
    }
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-neutral-700 mb-2 text-center">
        Kod weryfikacyjny
      </label>
      <div className="flex justify-center space-x-2">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="text"
            maxLength="1"
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onPaste={handlePaste}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="w-10 h-10 text-center text-lg border border-neutral-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            inputMode="numeric"
            pattern="[0-9]*"
          />
        ))}
      </div>
      <div className="mt-4 flex justify-center">
        <button
          onClick={handleManualVerification}
          className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
        >
          Sprawdź kod
        </button>
      </div>
      {(error || showIncompleteError) && (
        <div className="mt-2 text-sm text-red-600 text-center">
          {showIncompleteError ? 'Kod weryfikacyjny nie jest kompletny' : error}
        </div>
      )}
    </div>
  );
};

const LoginForm = ({ onSubmit, isLoading, error, registrationData, navigate }) => {
  const { login, registerInit, registerVerify } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [verificationId, setVerificationId] = useState(null);

  useEffect(() => {
    if (registrationData) {
      setShowVerification(true);
    }
  }, [registrationData]);

  const handleRegisterInit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      notifyError('Hasła nie są identyczne');
      return;
    }

    try {
      const result = await registerInit(email, password);
      if (result.success) {
        setVerificationId(result.verificationId);
        setShowVerification(true);
        onSubmit({ message: result.message });
      }
    } catch (error) {
      notifyError(error.message);
    }
  };

  const handleVerification = async (verificationCode) => {
    try {
      const success = await registerVerify(verificationId, verificationCode);
      if (success) {
        notifySuccess('Konto zostało pomyślnie utworzone!');
        setTimeout(() => {
          setIsRegistering(false);
          setShowVerification(false);
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error) {
      notifyError(error.message);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const success = await login(email, password);
      if (success) {
        notifySuccess('Zalogowano pomyślnie!');
        navigate('/dashboard');
      }
    } catch (error) {
      notifyError(error.message);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isRegistering) {
      handleRegisterInit(e);
    } else {
      handleLogin(e);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-neutral-50 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-primary-600 mb-2">Klint</h1>
          <p className="text-sm text-neutral-500">Ucz się słówek i rozwijaj swoje umiejętności językowe</p>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-neutral-200">
          <h2 className="text-xl font-semibold mb-6 text-center">
            {showVerification ? 'Weryfikacja emaila' : (isRegistering ? 'Zarejestruj się' : 'Zaloguj się')}
          </h2>
          
          {showVerification ? (
            <div>
              <p className="text-sm text-neutral-600 mb-4 text-center">
                Wprowadź 6-cyfrowy kod weryfikacyjny wysłany na adres<br /><strong>{email}</strong>
              </p>
              <VerificationCodeInput 
                onComplete={handleVerification} 
                error={verificationError}
              />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1">
                  Hasło
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  required
                />
              </div>
              
              {isRegistering && (
                <div className="mb-4">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 mb-1">
                    Potwierdź hasło
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                    required
                  />
                </div>
              )}
              
              {error && (
                <div className="mb-4 p-3 text-sm bg-red-50 text-red-600 rounded-md">
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Przetwarzanie...' : isRegistering ? 'Zarejestruj się' : 'Zaloguj się'}
              </button>
            </form>
          )}
          
          {!showVerification && (
            <button
              type="button"
              onClick={() => setIsRegistering(!isRegistering)}
              className="w-full mt-4 text-sm text-primary-600 hover:text-primary-700"
            >
              {isRegistering ? 'Masz już konto? Zaloguj się' : 'Nie masz konta? Zarejestruj się'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginForm; 